import { useState } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { SiteLayout } from '../../components/SiteLayout';
import { Icon } from '../../components/Icon';
import { Notice } from '../../components/ui';
import { CheckoutError } from '../../components/EmailVerify';
import { usePageMeta } from '../../lib/meta';
import { CATALOG, PLANS, PLAN_IDS, maxYearlySavingPct, isPaid, type Interval, type PaidPlanId, type PlanId } from '../../lib/plans';
import { formatBytes, formatMoney } from '../../lib/format';
import { CHECKOUT_UNAVAILABLE, startCheckout, useCheckoutAvailable } from '../../lib/checkout';
import { useSession } from '../../session';

type Lim = (typeof PLANS)['free']['limits'];
const n = (v: number) => v.toLocaleString('en-US');
const hours = (h: number) => (h >= 48 ? `${h / 24} days` : `${h} hours`);

/** Every row is generated from shared/plans.json limits; 0 means "not included". */
export const COMPARE: { label: string; value: (l: Lim) => string | number | boolean }[] = [
  { label: 'Download quality', value: () => 'Source quality' },
  { label: 'Cloud downloads per day', value: (l) => n(l.hosted_jobs_per_day) },
  { label: 'Cloud file size', value: (l) => `Up to ${formatBytes(l.hosted_max_bytes)}` },
  { label: 'Cloud file retention', value: (l) => hours(l.hosted_retention_hours) },
  { label: 'AI transcription', value: (l) => l.transcription_minutes_per_month > 0 && `${n(l.transcription_minutes_per_month)} min / month` },
  { label: 'AI summaries', value: (l) => l.summaries_per_month > 0 && `${n(l.summaries_per_month)} / month` },
  { label: 'AI-assisted searches', value: (l) => l.ai_discovery_per_day > 0 && `${n(l.ai_discovery_per_day)} / day` },
  { label: 'Subtitle translations', value: (l) => l.translations_per_month > 0 && `${n(l.translations_per_month)} / month` },
  { label: 'Discovery requests', value: (l) => `${n(l.discovery_requests_per_day)} / day` },
  { label: 'Library size', value: (l) => `${n(l.library_items)} items` },
];

const price = (id: PlanId, i: Interval) => (id === 'free' ? 0 : PLANS[id as PaidPlanId].prices[i]);

export function Pricing() {
  usePageMeta('Pricing', `Ayonix Studio plans: Free, ${PLANS.pro.name} at ${formatMoney(PLANS.pro.prices.month, CATALOG.currency)}/month and ${PLANS.studio.name} at ${formatMoney(PLANS.studio.prices.month, CATALOG.currency)}/month. Save up to ${maxYearlySavingPct}% yearly.`);
  const { me } = useSession();
  const { query, route } = useLocation();
  const [interval, setPeriod] = useState<Interval>('month');
  const [busy, setBusy] = useState<PaidPlanId | null>(null);
  const [error, setError] = useState<unknown>(null);
  const checkout = useCheckoutAvailable();
  const paid = isPaid(me);

  const subscribe = async (plan: PaidPlanId) => {
    setError(null);
    if (!me) { route(`/signup?next=${encodeURIComponent(`checkout:${plan}:${interval}`)}`); return; }
    setBusy(plan);
    try { await startCheckout(plan, interval); } catch (e) { setError(e); setBusy(null); }
  };

  return (
    <SiteLayout>
      <div class="container pricing">
        <header class="center page-hero">
          <h1>Choose Your Plan</h1>
          <p class="muted">Start free and upgrade anytime. Every plan saves media at the source’s real quality.</p>
          <div class="row" style="justify-content:center;gap:12px">
            <div class="pill-toggle" role="group" aria-label="Billing period">
              <button type="button" aria-pressed={interval === 'month'} onClick={() => setPeriod('month')}>Monthly</button>
              <button type="button" aria-pressed={interval === 'year'} onClick={() => setPeriod('year')}>Yearly</button>
            </div>
            {maxYearlySavingPct > 0 && <span class="save-pill" data-testid="save-pill">Save up to {maxYearlySavingPct}%</span>}
          </div>
        </header>
        {query.checkout === 'cancelled' && <Notice tone="info">Checkout was cancelled. You haven’t been charged.</Notice>}
        {!!error && <div style="margin-top:12px"><CheckoutError error={error} /></div>}

        <div class="plan-cards">
          {PLAN_IDS.map((id) => {
            const p = PLANS[id];
            const featured = id === 'pro';
            const amount = price(id, interval);
            const current = me?.entitlement.plan === id;
            return (
              <section key={id} class={`plan-card${featured ? ' featured' : ''}`} aria-labelledby={`plan-${id}-h`} data-testid={`plan-${id}`}>
                {featured && <span class="popular">Most Popular</span>}
                <h2 id={`plan-${id}-h`}>{p.name}</h2>
                <div class="price" data-testid={`price-${id}`}>{formatMoney(amount, CATALOG.currency)}<small> / {id === 'free' ? 'month' : interval}</small></div>
                <p class="muted small plan-tag">{id !== 'free' && interval === 'year'
                  ? <span data-testid={`permonth-${id}`}>{formatMoney(Math.round(amount / 12), CATALOG.currency)} per month, billed yearly</span>
                  : p.tagline}</p>
                <ul>{p.features.map((f) => <li key={f}><Icon name="check" size={16} />{f}</li>)}</ul>
                {id === 'free' ? (
                  <a class="btn outline block" href={me ? '/' : '/signup'}>{!me ? 'Get Started' : current ? 'Your current plan' : 'Open the web app'}</a>
                ) : current ? (
                  <a class="btn block" href="/app/account">Your plan — manage billing</a>
                ) : paid ? (
                  <a class="btn block" href="/app/account" data-testid={`subscribe-${id}`}>Change plan in billing</a>
                ) : (
                  <button class="btn primary block" type="button" data-testid={`subscribe-${id}`} onClick={() => subscribe(id as PaidPlanId)}
                    disabled={!!busy || checkout.available !== true} aria-describedby={checkout.available === false ? 'checkout-reason' : undefined}>
                    {busy === id ? 'Opening secure checkout…' : checkout.available === null ? 'Checking availability…' : 'Subscribe'}
                  </button>
                )}
              </section>
            );
          })}
        </div>
        {checkout.available === false && <p id="checkout-reason" class="hint center" style="margin-top:12px" data-testid="checkout-reason">{CHECKOUT_UNAVAILABLE}</p>}
        <ul class="trust-row" aria-label="Billing">
          {checkout.available && <li data-testid="trust-stripe"><Icon name="lock" size={18} />Secure payment by Stripe</li>}
          <li><Icon name="clock" size={18} />Cancel anytime in the Billing Portal</li>
        </ul>

        <section class="section" aria-labelledby="compare-h">
          <h2 id="compare-h" class="section-title center">Compare All Features</h2>
          <div class="table-wrap compare">
            <table data-testid="compare">
              <thead><tr><th scope="col">Feature</th>{PLAN_IDS.map((id) => <th key={id} scope="col" class={id === 'pro' ? 'hl' : undefined}>{PLANS[id].name}</th>)}</tr></thead>
              <tbody>
                {COMPARE.map((row) => (
                  <tr key={row.label}>
                    <th scope="row">{row.label}</th>
                    {PLAN_IDS.map((id) => {
                      const v = row.value(PLANS[id].limits as Lim);
                      return <td key={id}>{v === false ? <span class="no"><Icon name="x" size={16} title="Not included" /></span> : v}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p class="caption muted center" style="margin-top:16px">
            Prices in {CATALOG.currency.toUpperCase()}, excluding applicable taxes. Renews automatically until cancelled. Plan catalog {CATALOG.version}. See <a href="/terms">terms &amp; refunds</a> · Teams: <a href="mailto:sales@ayonix.com?subject=Ayonix%20Studio%20Business">Contact sales</a>.
          </p>
        </section>
      </div>
    </SiteLayout>
  );
}
